// src/aiq/components/NotificationPanel.tsx

import React, { useState } from 'react';
import { IconClose, IconBell, IconTrash, IconCheckCircle, IconShield, IconOps, IconUserGroup } from './Icons';
import { useAiQ } from '../state/AiQState';

interface NotificationPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
    const { notifications, markAllRead, clearNotifications } = useAiQ();
    const [activeTab, setActiveTab] = useState<'alerts' | 'audit'>('alerts');

    if (!isOpen) return null;

    const filteredNotes = notifications.filter(n => 
        activeTab === 'alerts' ? n.type !== 'audit' : n.type === 'audit'
    );

    return (
        <>
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-[60]"
                onClick={onClose}
            ></div>

            {/* Drawer */}
            <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl z-[70] flex flex-col border-l border-slate-200 animate-slide-left">
                
                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-slate-50 flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-slate-800 font-bold text-lg">
                            <IconBell />
                            <span>AiQ Center</span>
                        </div>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><IconClose /></button>
                    </div>
                    
                    {/* Tab Switcher */}
                    <div className="flex bg-slate-200 p-1 rounded-lg">
                        <button 
                            onClick={() => setActiveTab('alerts')}
                            className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                                activeTab === 'alerts' ? 'bg-white text-blue-600 shadow' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            Alerts ({notifications.filter(n => n.type !== 'audit' && !n.read).length})
                        </button>
                        <button 
                            onClick={() => setActiveTab('audit')}
                            className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                                activeTab === 'audit' ? 'bg-white text-purple-600 shadow' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            Audit Log
                        </button>
                    </div>
                </div>

                {/* List Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar bg-slate-50/50">
                    {filteredNotes.length === 0 ? (
                        <div className="text-center text-slate-400 mt-20 text-sm">
                            <div className="opacity-20 mb-2 scale-150 inline-block"><IconBell /></div>
                            <p>No records found.</p>
                        </div>
                    ) : (
                        filteredNotes.map((note) => (
                            <div 
                                key={note.id} 
                                className={`relative pl-4 p-3 rounded-lg border text-sm shadow-sm transition-all ${
                                    note.type === 'critical' ? 'bg-red-50 border-red-200 text-red-900' :
                                    note.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                                    note.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' :
                                    note.type === 'audit' ? 'bg-slate-50 border-slate-200 text-slate-700 font-mono text-xs' :
                                    'bg-white border-slate-200 text-slate-700'
                                }`}
                            >
                                {/* Timeline Connector (Visual) */}
                                {activeTab === 'audit' && (
                                    <div className="absolute left-[-18px] top-4 w-2 h-2 rounded-full bg-slate-300 border-2 border-white"></div>
                                )}

                                <div className="flex justify-between items-start mb-1">
                                    <div className="flex items-center gap-2">
                                        {note.module && <span className="text-[10px] uppercase font-bold opacity-60 bg-black/5 px-1.5 rounded">{note.module}</span>}
                                        {note.user && activeTab === 'audit' && <span className="text-[10px] font-bold text-blue-600">{note.user}</span>}
                                    </div>
                                    <span className="text-[10px] opacity-50 font-mono">{note.timestamp}</span>
                                </div>
                                <p className="leading-snug font-medium">{note.message}</p>
                            </div>
                        ))
                    )}
                    
                    {/* Timeline vertical line for audit mode */}
                    {activeTab === 'audit' && filteredNotes.length > 0 && (
                        <div className="absolute left-[21px] top-24 bottom-20 w-px bg-slate-200 -z-10"></div>
                    )}
                </div>

                <div className="p-4 border-t border-slate-200 bg-white flex justify-between">
                    <button 
                        onClick={clearNotifications}
                        className="text-xs font-medium text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                    >
                        <IconTrash /> Clear History
                    </button>
                    <button 
                        onClick={markAllRead}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors"
                    >
                        <IconCheckCircle /> Mark Read
                    </button>
                </div>
            </div>
        </>
    );
};

export default NotificationPanel;